import { Link } from "react-router-dom";
import Seo from "../components/Seo";
import CaptionImage from "../components/CaptionImage";
import Marquee from "../components/Marquee";
import { Reveal, MaskedLines, Chapter } from "../components/Motion";
import { IMAGES, CHARTS, IMPACT_STATS, PROGRAMS } from "../site/content";
import { ArrowRight } from "lucide-react";

export default function OurWork() {
  return (
    <>
      <Seo
        title="Our Work — Community Impact in Rural India"
        description="A small team. One mission. Real change in rural India — healthcare access, learning support, relief, community development, and language access. Organized by need, not by religion."
        path="/our-work"
      />

      <section className="bg-impact pt-28 sm:pt-36 pb-16 sm:pb-24" data-testid="impact-hero">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid items-center gap-12 lg:grid-cols-12">
            <div className="lg:col-span-7">
              <p className="eyebrow !text-sand/80 flex items-center gap-3">
                <span className="h-px w-8 bg-sand" aria-hidden="true" />
                ProOne nonprofit · registered late 2025
              </p>
              <MaskedLines
                lines={[
                  "A small team. One mission.",
                  <span key="t" className="italic text-sand">Real change in rural India.</span>,
                ]}
                className="mt-6 font-serif text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.08] text-cream"
              />
              <Reveal delay={0.55}>
                <p className="mt-6 max-w-xl text-base sm:text-lg leading-relaxed text-cream/80">
                  We work alongside local communities on healthcare access, learning,
                  and urgent needs. This is the honest half of ProOne — separate from
                  the services we sell, and held to a humbler standard of speech.
                </p>
                <div className="mt-9 flex flex-wrap gap-3">
                  <Link to="/support?tab=donate" className="btn-light" data-testid="impact-donate-btn">
                    Support the work <ArrowRight size={16} aria-hidden="true" />
                  </Link>
                  <Link to="/about" className="btn-ghost-light" data-testid="impact-about-btn">
                    Meet the people
                  </Link>
                </div>
              </Reveal>
            </div>
            <div className="lg:col-span-5">
              <Reveal delay={0.3}>
                <CaptionImage image={IMAGES.impactHero} ratio="aspect-[4/5]" rounded="rounded-[2rem]" dark priority />
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      <Marquee dark />

      <section id="reality" className="py-16 sm:py-24" data-testid="reality-section">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <Chapter number="01" eyebrow="Context" title="The Reality We Work In" />
          <div className="grid gap-10 lg:grid-cols-12">
            <div className="lg:col-span-7">
              <Reveal>
                <p className="text-base sm:text-lg leading-relaxed text-charcoal" data-testid="reality-opening">
                  <strong className="text-teal">India is home to many faiths and cultures. Our field work is not
                  organized by religion. It is organized by need. The sharpest gaps remain rural.</strong>
                </p>
                <p className="mt-5 text-sm sm:text-base leading-relaxed text-charcoal-muted">
                  The subcontinent's long history includes indigenous traditions, later
                  arrivals of other faiths, Islamic rule from around 1200 CE, and British
                  Crown rule from 1858 to 1947. What shapes our work is the present:
                  distance, cost, and missing services.
                </p>
              </Reveal>
            </div>
            <div className="lg:col-span-5">
              <Reveal delay={0.1}>
                <div className="space-y-4">
                  <blockquote className="rounded-2xl border-l-4 border-teal bg-sand/70 p-6" data-testid="inclusive-line">
                    <p className="font-serif text-lg italic leading-relaxed text-charcoal">
                      "We serve people across every faith. Poverty, distance, and missing
                      services decide where we go."
                    </p>
                  </blockquote>
                  <p className="text-sm leading-relaxed text-charcoal-muted" data-testid="focus-line">
                    We concentrate on rural communities that lack clinics, reliable schools,
                    clean water, and information in a language people speak.
                  </p>
                </div>
              </Reveal>
            </div>
          </div>

          <div className="mt-14 grid gap-4 sm:grid-cols-2 lg:grid-cols-5" data-testid="impact-stats-grid">
            {IMPACT_STATS.map((s, i) => (
              <Reveal key={s.label} delay={i * 0.06}>
                <div className="h-full rounded-2xl border border-line bg-white/70 p-5">
                  <p className="font-mono text-2xl sm:text-[1.7rem] font-medium tracking-tight text-teal">{s.metric}</p>
                  <p className="mt-1.5 font-mono text-[11px] uppercase tracking-[0.18em] text-charcoal/60">{s.label}</p>
                  <p className="mt-3 text-xs leading-relaxed text-charcoal-muted">{s.caption}</p>
                </div>
              </Reveal>
            ))}
          </div>

          <div className="mt-14 grid gap-6 md:grid-cols-2" data-testid="charts-gallery">
            {CHARTS.map((c, i) => (
              <Reveal key={c.id} delay={(i % 2) * 0.08}>
                <figure className="h-full rounded-3xl border border-line bg-white p-5 sm:p-7 shadow-sm" data-testid={`chart-card-${c.id}`}>
                  <img src={c.src} alt={c.title} loading="lazy" className="w-full h-auto rounded-xl" />
                  <figcaption className="mt-5">
                    <p className="font-serif text-lg font-semibold text-charcoal">{c.title}</p>
                    <p className="caption-text mt-2.5 flex items-start gap-3">
                      <span className="mt-[7px] h-px w-6 shrink-0 bg-current" aria-hidden="true" />
                      <span>{c.caption}</span>
                    </p>
                  </figcaption>
                </figure>
              </Reveal>
            ))}
          </div>

          <Reveal>
            <p className="mt-12 text-center font-serif text-xl sm:text-2xl italic text-teal" data-testid="reality-caption">
              "These are neighbors and children, not abstract averages."
            </p>
          </Reveal>
        </div>
      </section>

      <section id="programs" className="py-16 sm:py-24 bg-sand/60 border-y border-line" data-testid="programs-section">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <Chapter number="02" eyebrow="Programs" title="What the work looks like, program by program." />
          <div className="space-y-10">
            {PROGRAMS.map((p, i) => (
              <Reveal key={p.id}>
                <div
                  className={`grid items-stretch gap-0 overflow-hidden rounded-3xl border border-line bg-cream md:grid-cols-12 ${
                    i % 2 === 1 ? "md:[&>*:first-child]:order-2" : ""
                  }`}
                  data-testid={`program-card-${p.id}`}
                >
                  <div className="md:col-span-5 min-h-[220px] md:min-h-[280px]">
                    <img src={p.img.url} alt={p.img.alt} loading="lazy" className="h-full w-full object-cover" />
                  </div>
                  <div className="md:col-span-7 flex flex-col justify-center p-7 sm:p-10">
                    <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-teal">Program {p.index}</p>
                    <p className="mt-3 text-base sm:text-lg leading-relaxed text-charcoal">
                      <strong className="font-serif text-xl sm:text-2xl text-teal">{p.title}</strong>
                      <span className="mx-2 text-charcoal/40">—</span>
                      <strong>{p.lead}</strong>{" "}{p.body}
                    </p>
                    <p className="caption-text mt-6 flex items-start gap-3">
                      <span className="mt-[7px] h-px w-6 shrink-0 bg-current" aria-hidden="true" />
                      {p.img.caption}
                    </p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 sm:py-24" data-testid="impact-cta-section">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <Reveal>
            <div className="relative overflow-hidden rounded-[2.5rem] bg-impact px-6 py-16 sm:px-14 text-center">
              <div className="absolute -right-24 -top-24 h-72 w-72 rounded-full bg-sand/10 blur-3xl" aria-hidden="true" />
              <h2 className="mx-auto max-w-2xl font-serif text-2xl sm:text-3xl lg:text-4xl font-semibold leading-snug text-cream">
                Give where the gap is sharpest.
              </h2>
              <p className="mx-auto mt-5 max-w-xl text-sm sm:text-base leading-relaxed text-cream/75">
                Choose a designation — medical, education, relief, community — or let us
                direct your gift where it is most needed.
              </p>
              <div className="mt-9 flex flex-wrap justify-center gap-3">
                <Link to="/support?tab=donate" className="btn-light" data-testid="impact-cta-donate-btn">Donate</Link>
                <Link to="/contact?intent=Volunteer" className="btn-ghost-light" data-testid="impact-cta-volunteer-btn">Volunteer with us</Link>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
